import React from "react";

import DynamicAdminTable, { Column } from "../../components/admin-table";
import Topbar from "../../components/topbar";
import { useListSearch, ListTitle } from "../../components/ui/list-search";
import AddCompanyPopup from "../../components/popup/add-company";
import DeletePopup from "../../components/popup/delete";
import AddUserPopup from "../../components/popup/add-user";
import CompanyDetailsModal from "../../components/popup/company-details";

import Pagination from "../../components/pagination";
import AddBtn from "../../components/ui/add-btn";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem, Radio, TextField, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery, useTheme } from "@mui/material";
import { MoreHorizontal } from "lucide-react";
import BottomSheetDialog from "../../components/popup/mobile/mobile-bottom-sheet";
import Cookies from 'js-cookie';
import LoadingOverlay from "../../components/loading-overlay";
import Toggle from "../../components/ui/toggle";
import impersonateImage from '../../files/images/impersonate.png';
import companyImage from '../../files/images/company.png';
import { useToast } from "../../providers/toaster-provider";

import useApi from "../../hooks/use-api";
import buildApiRequestConfig from "../../api/url-builder";
import routes from "../../api/routes";

const PAGE_SIZE = 10;

// What the search matches on - the visible company details.
const companySearchText = (row: Record<string, any>) => [row.company, row.country, row.phone, row.loginNotifyEmail];

// Filter presets above the table - same pattern as Subscription Transactions.
const PRESETS: { key: string; label: string; match: (row: Record<string, any>) => boolean }[] = [
    { key: 'all', label: 'All', match: () => true },
    { key: 'permitted', label: 'Login Permitted', match: row => !!row.access },
    { key: 'restricted', label: 'Login Restricted', match: row => !row.access },
    { key: 'closed', label: 'Closed', match: row => !row.account },
    { key: 'alerts', label: 'Login Alerts On', match: row => !!row.loginNotifyEnabled },
];

type Company = {
    company: string;
    country: string;
    phone: string;
    banks: number;
    trx: number;
    payment: string;
    created: string;
    lastLogin: string;
    access: boolean;
    account: boolean;
    loginNotifyEnabled: boolean;
    loginNotifyEmail: string;
};

const Company: React.FC = () => {
    const { showSuccess, showError } = useToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    // Popups: the shared bottom sheet on a phone (like the app's other mobile
    // modals), a centered dialog otherwise.
    const renderDialog = (open: boolean, onClose: () => void, maxWidth: 'xs' | 'sm', children: React.ReactNode) => (isMobile
        ? <BottomSheetDialog open={open} onClose={onClose} fitContent>{children}</BottomSheetDialog>
        : <Dialog open={open} onClose={onClose} fullWidth maxWidth={maxWidth} PaperProps={{ sx: { borderRadius: 3 } }}>{children}</Dialog>);
    // Mobile sheet buttons: grey Cancel and the main action, side by side.
    const dialogActionsSx = isMobile ? { justifyContent: 'space-between', gap: 1, px: 3, pb: 2.5 } : { px: 3, pb: 2 };
    const cancelBtnSx = isMobile
        ? { flex: 1, textTransform: 'none', background: 'var(--color-container-background)', color: 'var(--color-text-muted)', boxShadow: 'none', borderRadius: 2, fontWeight: 600, '&:hover': { background: 'var(--color-container-background)' } }
        : { textTransform: 'none' };
    const mainBtnSx = isMobile
        ? { flex: 1, textTransform: 'none', boxShadow: 'none', borderRadius: 2, fontWeight: 600, whiteSpace: 'nowrap' }
        : { textTransform: 'none', boxShadow: 'none' };
    // Phone layout: the row whose actions menu is open.
    const [cardMenu, setCardMenu] = React.useState<{ anchor: HTMLElement; row: Record<string, any> } | null>(null);
    const [page, setPage] = React.useState(1);
    const [preset, setPreset] = React.useState('all');
    const [data, setData] = React.useState<Company[]>([]);
    const [openModal, setOpenModal] = React.useState<string>('');
    const [detailsCompany, setDetailsCompany] = React.useState<Record<string, any> | null>(null);

    const [fetch, setFetch] = useApi();
    const [update, setUpdate] = useApi();
    const [remove, setRemove] = useApi();
    const [toggleAccess, setToggleAccess] = useApi();
    const [toggleEnabled, setToggleEnabled] = useApi();
    const [saveLoginAlert, setSaveLoginAlert] = useApi();
    // Impersonate from a company's row: pick one of its users, and the session
    // opens in this company (same flow as the Impersonate page).
    const [impersonateCompany, setImpersonateCompany] = React.useState<Record<string, any> | null>(null);
    const [impersonateUserGuid, setImpersonateUserGuid] = React.useState('');
    const [fetchCompanyUsers, setFetchCompanyUsers] = useApi();
    const [startImpersonation, setStartImpersonation] = useApi();
    // Login alerts: the company whose alert address is being changed, and the address typed.
    const [alertDialog, setAlertDialog] = React.useState<{ row: Record<string, any>; email: string } | null>(null);

    React.useEffect(() => {
        const { url, method } = buildApiRequestConfig(routes.company.fetch);
        setFetch({ url, method });
    }, [page, update, remove, toggleAccess, toggleEnabled, saveLoginAlert]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetch;
        if (data && !isLoading) {
            setData(data as Company[]);
        }
        if (error) {
            showError('Error fetching company data');
        }
    }, [fetch]);

    React.useEffect(() => {
        const { data, isLoading, error } = toggleAccess;
        if (data && !isLoading) {
            const { url, method } = buildApiRequestConfig(routes.company.fetch);
            setFetch({ url, method });
        }
        if (error) {
            showError('Error updating access');
        }
    }, [toggleAccess, showError, setFetch]);

    React.useEffect(() => {
        const { data, isLoading, error } = toggleEnabled;
        if (data && !isLoading) {
            const { url, method } = buildApiRequestConfig(routes.company.fetch);
            setFetch({ url, method });
        }
        if (error) {
            showError('Error updating account status');
        }
    }, [toggleEnabled, showError, setFetch]);

    React.useEffect(() => {
        const { data, isLoading, error } = saveLoginAlert;
        if (data && !isLoading) {
            showSuccess(data.loginNotifyEnabled ? `Login alerts on - sending to ${data.loginNotifyEmail}` : 'Login alerts off');
            setAlertDialog(null);
        }
        if (error) showError(typeof error === 'string' ? error : 'Error updating login alerts');
    }, [saveLoginAlert]); // eslint-disable-line react-hooks/exhaustive-deps

    const openImpersonate = (row: Record<string, any>) => {
        setImpersonateCompany(row);
        setImpersonateUserGuid('');
        const { url, method } = buildApiRequestConfig(routes.impersonate.companyUsers, [{ param: 'entityId', value: row.id }]);
        setFetchCompanyUsers({ url, method });
    };
    const companyUsers: { guid: string; name: string | null; email: string; role: string }[] = impersonateCompany && fetchCompanyUsers.data?.users ? fetchCompanyUsers.data.users : [];

    // Admins come first - start with the first one picked.
    React.useEffect(() => {
        if (companyUsers.length && !impersonateUserGuid) setImpersonateUserGuid(companyUsers[0].guid);
    }, [fetchCompanyUsers.data]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        const { data, isLoading, error } = startImpersonation;
        if (data && !isLoading) {
            // useApi re-sends its last request when the token changes - clear
            // these first, or they go out again as the impersonated user
            // (not an admin) and fail with "Only admins can impersonate users".
            setStartImpersonation(null);
            setFetchCompanyUsers(null);
            Cookies.set('token', data.token);
            Cookies.set('user', JSON.stringify(data.user));
            Cookies.set('entities', JSON.stringify(data.entities || []));
            // A full load into the user portal, not an in-app navigate - the
            // identity just changed, and anything still holding the admin's
            // state (open requests, AuthGuard's timer on this /admin page)
            // could otherwise act on the new cookies and bounce to a login page.
            window.location.assign('/transaction');
        }
        if (error) showError(typeof error === 'string' ? error : 'Failed to start impersonation');
    }, [startImpersonation]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleStartImpersonation = () => {
        if (!impersonateCompany || !impersonateUserGuid) return;
        const { url, method } = buildApiRequestConfig(routes.impersonate.start);
        setStartImpersonation({ url, method, body: { targetUserGuid: impersonateUserGuid, entityId: impersonateCompany.id } });
    };

    const presetCounts = React.useMemo(() => Object.fromEntries(PRESETS.map(p => [p.key, data.filter(p.match).length])), [data]);
    const presetData = React.useMemo(() => data.filter((PRESETS.find(p => p.key === preset) || PRESETS[0]).match), [data, preset]);
    // Search within the chosen preset - by what's shown in the table.
    const list = useListSearch(presetData, companySearchText);
    const filteredData = list.filtered;
    React.useEffect(() => { setPage(1); }, [list.term]);

    const sendLoginAlert = (row: Record<string, any>, body: { enabled?: boolean; email?: string }) => {
        const { url, method } = buildApiRequestConfig(routes.company.loginNotification, [{ param: 'id', value: row.id }]);
        setSaveLoginAlert({ url, method, body });
    };

    // On sends to the company's address, or the admin inbox (the one new
    // signups go to) when it has none; off keeps the address for next time.
    const handleLoginAlertToggle = (row: Record<string, any>, value: boolean) => {
        sendLoginAlert(row, { enabled: value });
    };

    const alertEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(alertDialog?.email.trim() || '');

    const handleAccessToggle = (row: Record<string, any>, value: boolean) => {
        const { url, method } = buildApiRequestConfig(routes.company.toggleAccess, [{ param: 'id', value: row.id }]);
        setToggleAccess({ url, method });
        if (value) {
            showSuccess('Access enabled');
        } else {
            showSuccess('Access disabled');
        }
    };

    const handleAccountToggle = (row: Record<string, any>, value: boolean) => {
        const { url, method } = buildApiRequestConfig(routes.company.toggleEnabled, [{ param: 'id', value: row.id }]);
        setToggleEnabled({ url, method });
        if (value) {
            showSuccess('Account enabled');
        } else {
            showSuccess('Account disabled');
        }
    };

    const loginAlertCell = (row: Record<string, any>) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Toggle checked={!!row.loginNotifyEnabled} onChange={(val: boolean) => handleLoginAlertToggle(row, val)} />
            {row.loginNotifyEmail ? (
                <Typography
                    title="Change the address"
                    onClick={() => setAlertDialog({ row, email: row.loginNotifyEmail })}
                    sx={{ fontSize: 12, color: row.loginNotifyEnabled ? 'var(--color-primary)' : 'var(--color-text-muted)', cursor: 'pointer', textDecoration: 'underline', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                    {row.loginNotifyEmail}
                </Typography>
            ) : (
                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Off</Typography>
            )}
        </Box>
    );

    const pageRows = filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) as Record<string, any>[];
    const pagination = (
        <Pagination
            currentPage={page}
            totalPages={Math.ceil(filteredData.length / PAGE_SIZE)}
            onPageChange={setPage}
            pageSize={PAGE_SIZE}
            totalItems={filteredData.length}
        />
    );

    // Phone layout - one card per company: its details, the Access /
    // Account / Login Alerts toggles, and the row actions in a menu.
    const detail = (label: string, value: React.ReactNode) => (
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{label}</Typography>
            <Typography sx={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value === '' || value == null ? '-' : value}</Typography>
        </Box>
    );
    const shortDate = (v: any) => { if (!v) return ''; const d = new Date(v); return isNaN(d.getTime()) ? String(v) : d.toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }); };
    const toggleRow = (label: string, control: React.ReactNode) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, minHeight: 32 }}>
            <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', flexShrink: 0 }}>{label}</Typography>
            <Box sx={{ minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>{control}</Box>
        </Box>
    );
    const companyCards = (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {pageRows.length === 0 && (
                <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', textAlign: 'center', py: 4 }}>No companies</Typography>
            )}
            {pageRows.map(row => (
                <Box key={row.id} sx={{ background: '#fff', borderRadius: 2, border: '1px solid #e9e9e9', p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }} onClick={() => setDetailsCompany(row)}>
                            <Typography sx={{ fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.company}</Typography>
                            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{[row.country, row.phone].filter(Boolean).join(' · ') || '-'}</Typography>
                        </Box>
                        <IconButton size="small" aria-label={`Actions for ${row.company}`} onClick={e => setCardMenu({ anchor: e.currentTarget, row })}>
                            <MoreHorizontal size={18} />
                        </IconButton>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mt: 1.25 }}>
                        {detail('Bank Accounts', row.banks)}
                        {detail("TX's", row.trx)}
                        {detail('Payment', row.payment)}
                        <Box sx={{ gridColumn: 'span 3', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                            {detail('Created', shortDate(row.created))}
                            {detail('Last Login', shortDate(row.lastLogin))}
                        </Box>
                    </Box>
                    <Box sx={{ borderTop: '1px solid #f0f0f0', mt: 1.25, pt: 0.75 }}>
                        {toggleRow(row.access ? 'Login Permitted' : 'Login Restricted', <Toggle checked={!!row.access} onChange={(val: boolean) => handleAccessToggle(row, val)} />)}
                        {toggleRow(row.account ? 'Open' : 'Closed', <Toggle checked={!!row.account} onChange={(val: boolean) => handleAccountToggle(row, val)} />)}
                        {toggleRow('Login Alerts', loginAlertCell(row))}
                    </Box>
                </Box>
            ))}
            {filteredData.length > PAGE_SIZE && <Box sx={{ mt: 1 }}>{pagination}</Box>}
        </Box>
    );

    const columns: Column[] = [
        { id: 'company', label: 'Company', size: 100, onSort: (direction) => console.log(`Sort by createdAt ${direction}`) },
        { id: 'country', label: 'Country', size: 100, onSort: (direction) => console.log(`Sort by createdAt ${direction}`) },
        { id: 'phone', label: 'Contact Number', size: 100, onSort: (direction) => console.log(`Sort by amount ${direction}`) },
        { id: 'banks', label: 'Bank Accounts', size: 50, onSort: (direction) => console.log(`Sort by amount ${direction}`) },
        { id: 'trx', label: "TX's", size: 50, onSort: (direction) => console.log(`Sort by amount ${direction}`) },
        { id: 'payment', label: 'Payment Status', size: 150, onSort: (direction) => console.log(`Sort by amount ${direction}`) },
        { id: 'created', label: 'Created', size: 150, onSort: (direction) => console.log(`Sort by amount ${direction}`) },
        { id: 'lastLogin', label: 'Last Login', size: 150, onSort: (direction) => console.log(`Sort by amount ${direction}`) },
        { id: 'access', label: 'Access', size: 150, type: 'multi-toggle', sibling: 'account', toggleLabels: { on: 'Login Permitted', off: 'Login Restricted' }, onToggle: handleAccessToggle },
        { id: 'account', label: 'Account', size: 150, type: 'multi-toggle', sibling: 'access', toggleLabels: { on: 'Open', off: 'Closed' }, onToggle: handleAccountToggle },
        // Rendered below (loginAlertCell) - the toggle plus the address it sends to.
        { id: 'loginAlert', label: 'Login Alerts', size: 180 },
        {
            id: 'actions',
            label: 'Actions',
            size: 50,
            type: 'actions',
            onEdit: (row) => setDetailsCompany(row),
            editLabel: 'View',
            onDelete: (row) => { setOpenModal('delete'); console.log('Delete', row);  },
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <AddCompanyPopup
                open={openModal === 'add'}
                onClose={() => setOpenModal('')}
                onConfirm={(name, amount, enabled) => {
                    console.log('New company added:', { name, amount, enabled });
                    setOpenModal('');
                }}
            />
            <DeletePopup
                open={openModal === 'delete'}
                message="Delete Company?"
                description="This action will permanently remove the selected company."
                image={{ src: companyImage, width: 150, height: 150 }}
                onCancel={() => setOpenModal('')}
                onConfirm={() => {
                    setOpenModal('');
                }}
            />
            <LoadingOverlay
                isLoading={startImpersonation.isLoading}
                title="Starting Impersonation..."
                message="Please wait while we switch to the user account."
            />
            {renderDialog(!!impersonateCompany, () => setImpersonateCompany(null), 'sm', <>
                <DialogTitle sx={{ fontWeight: 700, ...(isMobile ? { fontSize: 22, pb: 1, wordBreak: 'break-word' } : {}) }}>Impersonate - {impersonateCompany?.company}</DialogTitle>
                <DialogContent sx={isMobile ? { pt: 1 } : undefined}>
                    <Box sx={{ display: 'flex', gap: isMobile ? 1.5 : 2, alignItems: 'center', mb: 2 }}>
                        <img src={impersonateImage} alt="" width={isMobile ? 44 : 64} height={isMobile ? 44 : 64} style={{ flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                            You'll be signed in as the selected user, in this company. Your admin session ends - sign in again here when you're done.
                        </Typography>
                    </Box>
                    {fetchCompanyUsers.isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
                    ) : companyUsers.length === 0 ? (
                        <Typography sx={{ fontSize: 13, py: 2, textAlign: 'center' }}>This company has no users to impersonate.</Typography>
                    ) : (
                        <Box sx={{ border: '1px solid #e9e9e9', borderRadius: 2, maxHeight: isMobile ? '45vh' : 320, overflowY: 'auto' }}>
                            {companyUsers.map(u => (
                                <Box
                                    key={u.guid}
                                    onClick={() => setImpersonateUserGuid(u.guid)}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, cursor: 'pointer', borderBottom: '1px solid #f0f0f0', '&:last-child': { borderBottom: 'none' }, background: impersonateUserGuid === u.guid ? 'rgba(var(--color-primary-rgb), 0.08)' : 'transparent' }}
                                >
                                    <Radio size="small" checked={impersonateUserGuid === u.guid} />
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography sx={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name || u.email}</Typography>
                                        {u.name && <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</Typography>}
                                    </Box>
                                    <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', textTransform: 'capitalize' }}>{u.role}</Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button variant={isMobile ? 'contained' : 'text'} onClick={() => setImpersonateCompany(null)} sx={cancelBtnSx}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={!impersonateUserGuid || startImpersonation.isLoading}
                        onClick={handleStartImpersonation}
                        sx={mainBtnSx}
                    >
                        Start Impersonation
                    </Button>
                </DialogActions>
            </>)}
            <AddUserPopup
                open={openModal === 'invite'}
                onClose={() => setOpenModal('')}
                onCreate={(newUser) => {
                    setOpenModal('');
                }}
            />
            <CompanyDetailsModal
                open={!!detailsCompany}
                company={detailsCompany}
                onClose={() => setDetailsCompany(null)}
            />
            {renderDialog(!!alertDialog, () => setAlertDialog(null), 'xs', <>
                <DialogTitle sx={{ fontWeight: 700, ...(isMobile ? { fontSize: 22, pb: 1, wordBreak: 'break-word' } : {}) }}>Login alerts - {alertDialog?.row.company}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', mb: 2 }}>
                        An email goes to this address whenever someone in this company signs in.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label="Send alerts to"
                        placeholder="name@example.com"
                        value={alertDialog?.email ?? ''}
                        onChange={e => setAlertDialog(prev => prev ? { ...prev, email: e.target.value } : prev)}
                        error={!!alertDialog?.email.trim() && !alertEmailValid}
                        helperText={alertDialog?.email.trim() && !alertEmailValid ? 'Enter a valid email address' : ' '}
                    />
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button variant={isMobile ? 'contained' : 'text'} onClick={() => setAlertDialog(null)} sx={cancelBtnSx}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={!alertEmailValid || saveLoginAlert.isLoading}
                        onClick={() => alertDialog && sendLoginAlert(alertDialog.row, { email: alertDialog.email.trim() })}
                        sx={mainBtnSx}
                    >
                        Save
                    </Button>
                </DialogActions>
            </>)}
            <Topbar title={<ListTitle title="Company" list={list} placeholder="Search companies..." />} />
            <Menu anchorEl={cardMenu?.anchor} open={!!cardMenu} onClose={() => setCardMenu(null)}>
                {[
                    { label: 'View', onClick: (row: Record<string, any>) => setDetailsCompany(row) },
                    { label: 'Impersonate', onClick: openImpersonate },
                    { label: 'Invite a User', onClick: () => setOpenModal('invite') },
                    { label: 'Delete', onClick: () => setOpenModal('delete') },
                ].map(a => (
                    <MenuItem
                        key={a.label}
                        onClick={() => { const row = cardMenu?.row; setCardMenu(null); if (row) a.onClick(row); }}
                        sx={{ fontSize: 14, ...(a.label === 'Delete' ? { color: 'var(--color-negative-amount)' } : {}) }}
                    >
                        {a.label}
                    </MenuItem>
                ))}
            </Menu>
            <div style={isMobile
                ? { padding: '10px', background: 'var(--color-page-background)', minHeight: 'calc(100vh - 86px)', boxSizing: 'border-box' }
                : { padding: '10px', background: 'var(--color-page-background)', height: 'calc(100vh - 86px)' }}>
                <Box mb={1} display="flex" justifyContent="space-between" alignItems="center" gap={isMobile ? 1 : 2} flexWrap={isMobile ? 'nowrap' : 'wrap'}>
                    <Box sx={isMobile ? { flex: 1, minWidth: 0, overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } } : {}}>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={preset}
                        onChange={(_, v) => { if (v) { setPreset(v); setPage(1); } }}
                        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: isMobile ? 1.25 : 1.75, whiteSpace: 'nowrap' } }}
                    >
                        {PRESETS.map(p => (
                            <ToggleButton key={p.key} value={p.key}>{p.label} ({presetCounts[p.key] ?? 0})</ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    </Box>
                    {isMobile ? (
                        <IconButton aria-label="Add Company" onClick={() => setOpenModal('add')} sx={{ flexShrink: 0, background: 'var(--color-primary)', color: '#fff', width: 36, height: 36, '&:hover': { background: 'var(--color-primary)' } }}>
                            <Typography sx={{ fontSize: 22, lineHeight: 1, fontWeight: 600 }}>+</Typography>
                        </IconButton>
                    ) : (
                        <AddBtn onClick={() => setOpenModal('add')} label="Add Company" />
                    )}
                </Box>
                {isMobile ? companyCards : (
                <DynamicAdminTable 
                    columns={columns} 
                    data={pageRows.map(row => ({ ...row, loginAlert: loginAlertCell(row) }))}
                    pagination={pagination}
                    additionalActions={[ 
                        { label: 'Impersonate', onClick: openImpersonate, icon: 'eye' },
                        { label: 'Invite a User', onClick: () => setOpenModal('invite'), icon: 'invite' },
                    ]}
                />
                )}
            </div>
        </div>
    );
};

export default Company;